import React, { useRef, useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import Main from "../redux/actions/main.action";
import { StateKeys } from "../redux/states/main.state";
import ApiEndpoint from "../services/api.endpoint.service";

export interface CustomAudioPlayerProps {
  /** Track title shown above the player (e.g. "1. What is sound?") */
  title: string;
  /** URL of the audio file */
  src: string;
  /** Content ID for posting listen points (optional) */
  contentId?: number;
  /** Resource ID for posting listen points (optional) */
  resourceId?: number;
  /** When true, track has been completed/heard; show replay icon and progress at end */
  isCompleted?: boolean;
  /** When true, this instance is the active/playing one (title in orange, others pause when another plays) */
  isActive?: boolean;
  /** Called when user requests play/replay so parent can set active player (e.g. pause others) */
  onPlayRequest?: () => void;
  /** Called when this track finishes playing (e.g. for autoplay next) */
  onEnded?: () => void;
  /** When true and this instance is active, start playback once (used by parent for autoplay next) */
  autoPlay?: boolean;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function CustomAudioPlayer({
  title,
  src,
  contentId,
  resourceId,
  isCompleted = false,
  isActive = false,
  onPlayRequest,
  onEnded: onEndedProp,
  autoPlay = false,
}: CustomAudioPlayerProps) {
  const dispatch = useDispatch();
  const audioRef = useRef<HTMLAudioElement>(null);
  const volumeControlRef = useRef<HTMLDivElement>(null);
  const pointPostedRef = useRef(false);
  const hasAutoPlayedRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoop, setIsLoop] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const audio = audioRef.current;

  const postListenPoint = useCallback(() => {
    if (pointPostedRef.current || contentId == null || resourceId == null)
      return;
    pointPostedRef.current = true;
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.USER_POINTS,
        apiEndpoint: ApiEndpoint.POINTS,
        req: {
          where: "listen",
          content_id: contentId,
          resource_id: resourceId,
          points: 1,
        },
      }),
    );
  }, [contentId, resourceId, dispatch]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onLoadedMetadata = () => setDuration(el.duration);
    const onEnded = () => {
      setIsPlaying(false);
      postListenPoint();
      onEndedProp?.();
    };
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("ended", onEnded);
    };
  }, [src, postListenPoint, onEndedProp]);

  useEffect(() => {
    pointPostedRef.current = false;
  }, [src, contentId, resourceId]);

  useEffect(() => {
    hasAutoPlayedRef.current = false;
  }, [src]);

  // Close volume control when clicking outside
  useEffect(() => {
    if (!showVolumeSlider) return;
    const handleClick = (e: MouseEvent) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(e.target as Node)) {
        setShowVolumeSlider(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [showVolumeSlider]);

  // When another player becomes active, pause this one
  useEffect(() => {
    if (!isActive && isPlaying) {
      const el = audioRef.current;
      if (el) el.pause();
      setIsPlaying(false);
    }
  }, [isActive, isPlaying]);

  // One-time auto-play when parent advances to this track (e.g. autoplay next)
  useEffect(() => {
    if (!autoPlay || !isActive || hasAutoPlayedRef.current) return;
    const el = audioRef.current;
    if (!el) return;
    hasAutoPlayedRef.current = true;
    onPlayRequest?.();
    el.play().then(() => setIsPlaying(true));
  }, [autoPlay, isActive, onPlayRequest]);

  useEffect(() => {
    if (!autoPlay) hasAutoPlayedRef.current = false;
  }, [autoPlay]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = playbackRate;
    el.loop = isLoop;
    el.muted = isMuted;
    el.volume = volume;
  }, [playbackRate, isLoop, isMuted, volume]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    onPlayRequest?.();
    if (isPlaying) {
      el.pause();
      setIsPlaying(false);
    } else {
      el.play();
      setIsPlaying(true);
    }
  };

  const handleReplay = () => {
    const el = audioRef.current;
    if (!el) return;
    onPlayRequest?.();
    el.currentTime = 0;
    setCurrentTime(0);
    el.play();
    setIsPlaying(true);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    el.currentTime = pct * duration;
    setCurrentTime(el.currentTime);
  };

  const cycleSpeed = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
    const i = rates.indexOf(playbackRate);
    const next = rates[(i + 1) % rates.length];
    setPlaybackRate(next);
  };

  // Completed tracks show progress at end (100%); others show actual progress
  const displayProgress = isCompleted
    ? 100
    : duration > 0
      ? (currentTime / duration) * 100
      : 0;

  return (
    <div
      className="d-flex flex-column gap-2 w-100"
      style={{
        borderBottom: "1px solid rgba(0,0,0,0.1)",
        paddingTop: "28px",
        paddingBottom: "14px",
      }}
    >
      <div
        className="fw-semibold"
        style={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "16px",
          letterSpacing: "0.216px",
          color: isActive ? "#cab39b" : "#3c3c3b",
          marginLeft: "24px",
          lineHeight: "20px",
          fontWeight: 600,
        }}
      >
        {title}
      </div>

      <div
        className="d-flex flex-column rounded px-3 py-3 w-100"
        style={{
          backgroundColor: "#fff",
          borderRadius: "10px",
        }}
      >
        <div className="d-flex align-items-center justify-content-between w-100 gap-2 flex-wrap">
          {isCompleted ? (
            <button
              type="button"
              className="btn btn-link p-0 border-0 text-decoration-none"
              style={{ color: "#3c3c3b", minWidth: "24px" }}
              onClick={handleReplay}
              aria-label="Replay"
            >
              <i className="las la-redo fs-4" />
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-link p-0 border-0 text-decoration-none"
              style={{ color: "#3c3c3b", minWidth: "24px" }}
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              <i className={`las la-${isPlaying ? "pause" : "play"} fs-4`} />
            </button>
          )}

          <button
            type="button"
            className="btn btn-link p-0 border-0 text-decoration-none"
            style={{
              color: isLoop ? "#E76E4B" : "#3c3c3b",
              minWidth: "24px",
            }}
            onClick={() => setIsLoop(!isLoop)}
            aria-label="Toggle loop"
          >
            <i className="las la-redo fs-5" />
          </button>

          <span
            className="text-nowrap"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12.6px",
              color: "#3c3c3b",
              lineHeight: "normal",
              minWidth: "70px",
            }}
          >
            {formatTime(currentTime)}/{formatTime(duration)}
          </span>

          <div
            className="flex-grow-1 d-flex align-items-center"
            style={{ minWidth: "80px", maxWidth: "400px" }}
          >
            <div
              role="progressbar"
              aria-valuenow={currentTime}
              aria-valuemin={0}
              aria-valuemax={duration}
              className="rounded-pill overflow-hidden"
              style={{
                height: "6px",
                backgroundColor: "rgba(0,0,0,0.1)",
                cursor: "pointer",
                width: "100%",
              }}
              onClick={handleSeek}
            >
              <div
                className="h-100 rounded-pill"
                style={{
                  width: `${displayProgress}%`,
                  backgroundColor: "#E76E4B",
                  transition: "width 0.1s linear",
                }}
              />
            </div>
          </div>

          <button
            type="button"
            className="btn btn-link p-0 border-0 text-decoration-none text-nowrap"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12.6px",
              color: "#3c3c3b",
              minWidth: "32px",
            }}
            onClick={cycleSpeed}
            aria-label="Playback speed"
          >
            {playbackRate}x
          </button>

          <div ref={volumeControlRef} className="position-relative d-flex align-items-center">
            {showVolumeSlider && (
              <div
                className="position-absolute d-flex flex-column align-items-center gap-2 rounded"
                style={{
                  bottom: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  marginBottom: "6px",
                  padding: "10px 8px",
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 10,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={isMuted ? 0 : volume}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setVolume(v);
                    setIsMuted(v === 0);
                  }}
                  aria-label="Volume"
                  style={{
                    width: "80px",
                    height: "6px",
                    cursor: "pointer",
                    accentColor: "#E76E4B",
                  }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 border-0 text-decoration-none"
                  style={{ color: "#3c3c3b", fontSize: "12px" }}
                  onClick={() => setIsMuted(!isMuted)}
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? "Unmute" : "Mute"}
                </button>
              </div>
            )}
            <button
              type="button"
              className="btn btn-link p-0 border-0 text-decoration-none"
              style={{ color: "#3c3c3b", minWidth: "24px" }}
              onClick={() => setShowVolumeSlider((v) => !v)}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              <i className={`las la-volume-${isMuted ? "mute" : "up"} fs-5`} />
            </button>
          </div>
        </div>
      </div>

      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
}

export default CustomAudioPlayer;
